import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useRef,
  useEffect,
} from "react";
import styles from "./pocContext.module.scss";
import { iGlobalContext } from "../globalContext/interface";
import { iHistoryContext } from "../historyContext/interface";
import { useHistoryContext } from "../historyContext/historyContext";
import { useNavigate } from "react-router-dom";
import { StateContext } from "../globalContext/context";
import { scrollToBottom } from "../../utils";
import { iRecommendedTripContext } from "../recommendedTripContext/recommendedTripContextInterface";
import { useRecommendedTripContext } from "../recommendedTripContext/recommendedTripContext";
import { toastMessage } from "../../utils/ToastProvider";
import { CHAT_BOT_NAME } from "../../constant/common";
import {
  CHAT_POC_RESPONSE_TYPE,
  chatPocApi,
  chatPocVoiceApi,
  iChatPocAPIPayload,
} from "../../api/poc-api/chatPOC";
import { iChatData } from "../../types";
import { useSocketContext } from "../socketContext/SocketContext";
import { v2orch } from "../../utils/v2OrchLog";
import { dropEmptyContentMessages } from "../../streaming/finalizeGuards";
import { getChatId, setChatId } from "../../utils/chat/chatId";
import { rotateChatId } from "../../utils/chat/rotateChatId";
import { isSelectionActiveInChat } from "../../utils/chat/chatSelection";
import { SELECTED_CHAT_DATA } from "../../constant/dummyData";
import { getSessionId } from "../../utils/sessionId";
import { SearchProgressStore } from "../../services/searchProgress/searchProgressStore";
import { isStreamedFlightClarifyError } from "../../services/searchProgress/legacyFlightError";
import { rollItineraryDatesToFuture } from "../../utils/chat/rollItineraryDatesToFuture";
import { appendErrorMessageTochat } from "../../utils/appendErrorMessageToChat";
import { ENVIRONMENT_TYPE, LLM_RESPONSE_ERROR } from "../../constant/common";
import { ROUTES } from "../../constant";
import { ScreenTypes } from "../../enums";
import { API_KEY, ENDPOINT, environment, VOICE_INPUT_MODE } from "../../constant/environment";
import { TOAST_MESSAGES } from "../../constant/messages/toastMessages";
import SpeechRecognition from "react-speech-recognition";
import { v4 } from "uuid";
import { iDataStreamingContext } from "../dataStreamingContext/dataStreamingContextInterface";
import { useDataStreamingContext } from "../dataStreamingContext/DataStreamingContext";
import toast from "react-hot-toast";
import axios from "axios";
import { iTopDestinationContext } from "../topDestinationContext/topDestinationContextInterface";
import {
  useTopDestinationContext,
  sanitizeItineraryForLLM,
} from "../topDestinationContext/TopDestinationContext";
import { encryptData, decryptData } from "../../utils/api/encryption";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../constant/google-analytics/GAEvents";
import { useTheresiasEventBridge } from "../../services/intent/useTheresiasEventBridge";
export interface iPocContext {
  handleNewTripSendMessage: any;
  handleTopDestinationSendMessage: any;
  init: any;
  transcription: any;
  formatText: any;
  stopVoice: any;
  isVoiceActive: boolean;
  status: string;
}
// V2-block helpers (extractAiAssistantTexts / findRenderPayload /
// normalizeHotelResponse) moved to `src/utils/v2BlockRenderer.ts` so both
// pocContext (legacy HTTP fallback, no current callers) and
// DataStreamingContext (WS v2_block finalize) share one implementation.

// Walks chatData[] backwards and returns the most recent orchestrator-decided
// `agents[0].key` (poc_chat / conversation_stream_new). Skips bypass + sibling
// keys (modify_itinerary_stream / show_more / chat_system_explore_city_msg /
// nearby_activity / nearby_restaurants / activity_details / askzoya_*). Returns
// null on cold start or when no orchestrator-decided turn has happened yet.
// Used to populate `priorAgentKey` in the outbound WS body — drives the
// orchestrator's short-continuation stickiness ("yes the cheaper one" after a
// poc_chat turn correctly sticks on poc_chat even though it's ambiguous).
const ORCH_DECIDED_KEYS = new Set(["poc_chat", "conversation_stream_new"]);
const ORCH_SKIPPED_KEYS = new Set([
  "modify_itinerary_stream",
  "show_more",
  "chat_system_explore_city_msg",
  "nearby_activity",
  "nearby_restaurants",
  "activity_details",
  "askzoya_hotels",
  "askzoya_flights",
]);
function derivePriorAgentKey(chatHistory: iChatData[] | undefined): {
  priorAgentKey: string | null;
  walkedEntries: number;
  skippedKeys: string[];
} {
  if (!Array.isArray(chatHistory) || chatHistory.length === 0) {
    return { priorAgentKey: null, walkedEntries: 0, skippedKeys: [] };
  }
  const skippedKeys: string[] = [];
  let walked = 0;
  for (let i = chatHistory.length - 1; i >= 0; i--) {
    walked++;
    const key = chatHistory[i]?.agents?.[0]?.key;
    if (!key) continue;
    if (ORCH_DECIDED_KEYS.has(key)) {
      return { priorAgentKey: key, walkedEntries: walked, skippedKeys };
    }
    if (ORCH_SKIPPED_KEYS.has(key)) {
      skippedKeys.push(key);
      continue;
    }
  }
  return { priorAgentKey: null, walkedEntries: walked, skippedKeys };
}

function deriveActiveItinerary(selectedChatData: any): {
  hasActiveItinerary: string | null;
  cityCount: number;
} {
  const cityWise: any[] = Array.isArray(selectedChatData?.city_wise_itinerary)
    ? selectedChatData.city_wise_itinerary
    : [];
  const destinations: any[] = Array.isArray(selectedChatData?.destinationList)
    ? selectedChatData.destinationList
    : [];
  const looksLikeItinerary =
    cityWise.length > 0 ||
    destinations.some(
      (c: any) =>
        (Array.isArray(c?.tripDetails) && c.tripDetails.length > 0) ||
        (typeof c?.city_name === "string" && c.city_name.length > 0)
    );
  if (!looksLikeItinerary) return { hasActiveItinerary: null, cityCount: 0 };
  const source = cityWise.length > 0 ? cityWise : destinations;
  const cities: string[] = source
    .map((c: any) => c?.city_name || c?.destination_city || c?.destination_name)
    .filter((n: any): n is string => typeof n === "string" && n.length > 0);
  if (cities.length === 0) return { hasActiveItinerary: null, cityCount: 0 };
  return { hasActiveItinerary: cities.join(", "), cityCount: cities.length };
}

function deriveActiveArtifacts(chatData: any): {
  hasActiveFlights: boolean;
  hasActiveStays: boolean;
} {
  let hasActiveFlights = false;
  let hasActiveStays = false;
  if (Array.isArray(chatData)) {
    for (const entry of chatData) {
      const flightResponse = entry?.flightResponse;
      if (
        flightResponse &&
        Array.isArray(flightResponse.sectors) &&
        flightResponse.sectors.length > 0 &&
        !isStreamedFlightClarifyError(entry)
      ) {
        hasActiveFlights = true;
      }
      const hotelResponse = entry?.hotelResponse;
      if (
        Array.isArray(hotelResponse) &&
        hotelResponse.some(
          (city: any) => Array.isArray(city?.hotels) && city.hotels.length > 0
        )
      ) {
        hasActiveStays = true;
      }
      if (hasActiveFlights && hasActiveStays) break;
    }
  }
  return { hasActiveFlights, hasActiveStays };
}

const PocContext = createContext({} as iPocContext);
export const PocContextProvider = ({ children }: { children: ReactNode }) => {
  const context = useContext(StateContext);
  const {
    socketId,
    chatInputText,
    setChatInputText,
    setShowMoreFlag,
    setOpenOverlayScreen,
    setChatData,
    setFirstScreen,
    setSharedItineraryId,
    setIsItinerarySavedFlag,
    setenableMultiSelect,
    setScreen,
    openDetailsSliderFlag,
    appConfig,
    selectedChatData,
    setSelectedChatData,
    isMobile,
    chatData,
    userInfo,
    accessJWT,
    desktopScrollContainerRef,
  }: iGlobalContext = context;
  const {
    setCallSaveHistoryAPIFlag,
    getHistory,
    callSaveHistoryAPIFlag,
    setCurrentHistoryId,
  }: iHistoryContext = useHistoryContext();
  useTopDestinationContext();
  const { scrollContainerRef }: iRecommendedTripContext =
    useRecommendedTripContext();
  const {
    handleConversationStreamEvents,
    disableInput,
    setDisableInput,
    chatAbortController,
    chatAbortControllerRef,
    handleFollowUpStreamEvents,
    outboundV2RequestRef,
  }: iDataStreamingContext = useDataStreamingContext();
  const { handleOpenSocketConnection } = useSocketContext();
  const navigate = useNavigate();
  const fireTheresiasEvent = useTheresiasEventBridge();

  const sendInFlightRef = useRef(false);
  useEffect(() => {
    if (!disableInput) sendInFlightRef.current = false;
  }, [disableInput]);

  // Main-chat send for a brand-new conversation. WS-only as of v2 orchestrator
  // wiring — payload goes to the LLM-function Lambda's `itinerary` requestType
  // handler, which either invokes the orchestrator (when origin is allowlisted)
  // or runs byte-equivalent to today. The Lambda streams the response back
  // over WS as `v2_block` frames (poc_chat route) or legacy intent-envelope
  // frames (conversation_stream_new route); both are finalized by
  // DataStreamingContext, which stamps `agents` + `router` + `traceId` on the
  // pending chat entry by id.
  async function handleNewTripSendMessage(promptOverride?: string, hideUserBubble?: boolean) {
    scrollToBottom(scrollContainerRef);
    setenableMultiSelect(false);
    const effectivePrompt = promptOverride ?? chatInputText;
    if (effectivePrompt.trim() === "") {
      toastMessage.info("Please type your message!");
      return;
    }

    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;

    rotateChatId();
    setCurrentHistoryId("");
    // Theresias `home-chat-send` event. Wired here (active path) — the
    // legacy wire in `TopDestinationContext.handleSendMessageStreaming`
    // is unreachable in the current UI flow.
    triggerGAEvent(GA_LABEL_ENUMS.chat_input_field_send_prompt_to_llm_click);
    fireTheresiasEvent("home-chat-send", { userMessage: effectivePrompt });
    setOpenOverlayScreen(true);
    setDisableInput(true);
    SpeechRecognition.stopListening();
    setShowMoreFlag(false);
    const abortController = new AbortController();
    chatAbortControllerRef.current = abortController;

    const reqId = v4();
    const traceId = reqId.slice(0, 8);
    const userEntryId = v4();
    const pendingEntryId = v4();

    // New turn = clean slate for the (purely Pusher-triggered) search-progress
    // stream. The prior search's done-state is already frozen onto its entry
    // (ChatContentNew), so this clears only the global store — preventing a stale
    // done-state from leaking onto this turn's bubble if no search events arrive.
    SearchProgressStore.reset();

    // Replace chatData with [userBubble, loadingPlaceholder]. Finalize step
    // (DataStreamingContext) replaces the loading entry by id.
    setChatData([
      {
        id: userEntryId,
        message: effectivePrompt,
        isUserMessage: true,
        prompt: [],
        isLoading: false,
        destinationList: [],
        singleSelectedDestination: false,
        shouldNotShowFirstMessage: hideUserBubble === true,
      },
      {
        id: pendingEntryId,
        message: "",
        prompt: [],
        isUserMessage: false,
        isLoading: true,
        destinationList: [],
        singleSelectedDestination: false,
      },
    ]);
    setSelectedChatData({ ...SELECTED_CHAT_DATA });
    setChatInputText("");

    // Navigate up front so the loading placeholder lands on the chat page.
    if (isMobile) navigate(ROUTES.TopDestination);
    else setFirstScreen(ScreenTypes.topDestination);
    setScreen("");

    const messages = [{ role: "user", content: effectivePrompt }];
    const origin = window.location.origin;
    const newConversation: iChatData[] = [];
    const { priorAgentKey, walkedEntries, skippedKeys } = derivePriorAgentKey(newConversation);
    const { hasActiveItinerary, cityCount } = deriveActiveItinerary(null);
    const { hasActiveFlights, hasActiveStays } = deriveActiveArtifacts(newConversation);

    v2orch("turn-start", {
      traceId,
      isNewTrip: true,
      userInputPreview: effectivePrompt.slice(0, 80),
      chatDataLen: chatData.length,
      pendingEntryId,
    });
    v2orch("derive.priorAgent", { traceId, priorAgentKey, walkedEntries, skippedKeys });
    v2orch("derive.activeItin", {
      traceId,
      hasActiveItinerary,
      cityCount,
      source: "new-conversation",
    });
    v2orch("derive.origin", { traceId, origin });
    v2orch("derive.auth", { traceId, hasJWT: !!accessJWT, jwtLen: accessJWT?.length ?? 0 });

    // Stash for DataStreamingContext.finalizeV2Turn — drives target chat entry
    // by id and the suggestion-fetch promptArray.
    outboundV2RequestRef.current = {
      messages,
      reqId,
      traceId,
      pendingEntryId,
      caller: "newTrip",
    };

    const body = {
      requestType: "itinerary" as const,
      chatId: getChatId() || "",
      iteneryInput: null,
      userLocation: null,
      userInput: effectivePrompt,
      messages,
      userId: userInfo?.id ? String(userInfo.id) : "",
      accessToken: accessJWT,
      origin,
      priorAgentKey,
      hasActiveItinerary,
      hasActiveFlights,
      hasActiveStays,
      traceId,
      // BE publishes streamed hotel search-progress to private-search-${sessionId};
      // same id the FE subscribes with (searchProgressChannel). Additive only.
      sessionId: getSessionId(),
    };
    v2orch("ws.send", {
      traceId,
      requestType: body.requestType,
      bodyKeys: Object.keys(body),
      bodyPreview: {
        origin,
        priorAgentKey,
        hasActiveItinerary,
        hasActiveFlights,
        hasActiveStays,
        userInputLen: effectivePrompt.length,
        messagesLen: messages.length,
        sessionId: body.sessionId,
      },
    });
    handleOpenSocketConnection(body);
    scrollToBottom(scrollContainerRef);
  }

  // Main-chat send for a continuation turn. Same payload shape as the new-trip
  // send; only difference is messages include prior turn context and the user
  // bubble is appended to (not replacing) chatData.
  //
  // `hideUserBubble` (mirrors `handleNewTripSendMessage`) suppresses the DISPLAY
  // of the user bubble only — the prompt is still sent to the LLM in `messages`.
  // For internal/system prompts the user never typed, e.g. the expired-results
  // dialog's "fetch refreshed hotel results". Do NOT use it for synthesized
  // prompts that legitimately describe the user's intent ("Find stays in Paris
  // from …", the in-chat select-card prompts) — those should stay visible.
  async function handleTopDestinationSendMessage(
    promptOverride?: string,
    hideUserBubble?: boolean,
    attachItinerary?: boolean,
  ) {
    toast.dismiss();
    if (chatData.length > 0 && chatData[chatData.length - 1].isLoading) {
      toastMessage.info("Please wait, we are still fetching the result!");
      return;
    }
    const effectivePrompt = promptOverride ?? chatInputText;
    if (effectivePrompt.trim() === "") {
      toastMessage.info("Please type your message!");
      return;
    }

    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;

    if (!getChatId()) {
      setChatId(v4());
    }
    // Theresias `home-chat-send` event. Wired here (active path) — the
    // legacy wire in `TopDestinationContext.handleSendMessageStreaming`
    // is unreachable in the current UI flow.
    triggerGAEvent(GA_LABEL_ENUMS.chat_input_field_send_prompt_to_llm_click);
    fireTheresiasEvent("home-chat-send", { userMessage: effectivePrompt });

    const reqId = v4();
    const traceId = reqId.slice(0, 8);
    const userEntryId = v4();
    const pendingEntryId = v4();

    // New turn = clean slate for the (purely Pusher-triggered) search-progress
    // stream (prior search already frozen onto its entry; this clears only the
    // global store so a stale done-state can't leak onto this turn's bubble).
    SearchProgressStore.reset();

    setChatData((prev: iChatData[]) => [
      ...prev,
      {
        id: userEntryId,
        message: effectivePrompt,
        isUserMessage: true,
        prompt: [],
        isLoading: false,
        destinationList: [],
        singleSelectedDestination: false,
        shouldNotShowFirstMessage: hideUserBubble === true,
      },
      {
        id: pendingEntryId,
        message: "",
        prompt: [],
        isUserMessage: false,
        isLoading: true,
        destinationList: [],
        singleSelectedDestination: false,
      },
    ]);
    setChatInputText("");
    scrollToBottom(desktopScrollContainerRef);
    setDisableInput(true);
    const abortController = new AbortController();
    chatAbortControllerRef.current = abortController;

    // Walk chatData history backwards for the most recent assistant context.
    const dupChatData: any[] = JSON.parse(JSON.stringify(chatData));
    const promptContext: any[] =
      dupChatData.reverse().find((item: any) => item?.prompt?.length > 0)?.prompt || [];

    const messages = dropEmptyContentMessages([
      ...promptContext,
      { role: "user", content: effectivePrompt },
    ]);
    const origin = window.location.origin;
    const { priorAgentKey, walkedEntries, skippedKeys } = derivePriorAgentKey(chatData);
    const turnChatId = getChatId();
    const selectionActive = isSelectionActiveInChat(selectedChatData, chatData, turnChatId);
    const activeItinerary = attachItinerary === true || selectionActive ? selectedChatData : null;
    if (attachItinerary === true && !selectionActive && selectedChatData?.id) {
      const attachedId = selectedChatData.id;
      setSelectedChatData((prev: any) =>
        prev?.id === attachedId ? { ...prev, attachedToChatId: turnChatId } : prev,
      );
    }
    const { hasActiveItinerary, cityCount } = deriveActiveItinerary(activeItinerary);
    const { hasActiveFlights, hasActiveStays } = deriveActiveArtifacts(chatData);

    v2orch("turn-start", {
      traceId,
      isNewTrip: false,
      userInputPreview: effectivePrompt.slice(0, 80),
      chatDataLen: chatData.length,
      pendingEntryId,
    });
    v2orch("derive.priorAgent", { traceId, priorAgentKey, walkedEntries, skippedKeys });
    v2orch("derive.activeItin", {
      traceId,
      hasActiveItinerary,
      cityCount,
      selectionActive,
      source: "selectedChatData.destinationList||city_wise_itinerary",
    });
    v2orch("derive.origin", { traceId, origin });
    v2orch("derive.auth", { traceId, hasJWT: !!accessJWT, jwtLen: accessJWT?.length ?? 0 });

    outboundV2RequestRef.current = {
      messages,
      reqId,
      traceId,
      pendingEntryId,
      caller: "topDestination",
    };

    const currentItineray = hasActiveItinerary
      ? rollItineraryDatesToFuture(sanitizeItineraryForLLM(activeItinerary))
      : undefined;
    v2orch("derive.currentItinerary", {
      traceId,
      attachItineraryRequested: !!attachItinerary,
      selectionActive,
      currentItineraryIncluded: !!currentItineray,
    });

    const body = {
      ...(currentItineray ? { currentItineray } : {}),
      requestType: "itinerary" as const,
      chatId: getChatId() || "",
      iteneryInput: null,
      userLocation: null,
      userInput: effectivePrompt,
      messages,
      userId: userInfo?.id ? String(userInfo.id) : "",
      accessToken: accessJWT,
      origin,
      priorAgentKey,
      hasActiveItinerary,
      hasActiveFlights,
      hasActiveStays,
      traceId,
      // BE publishes streamed hotel search-progress to private-search-${sessionId};
      // same id the FE subscribes with (searchProgressChannel). Additive only.
      sessionId: getSessionId(),
    };
    v2orch("ws.send", {
      traceId,
      requestType: body.requestType,
      bodyKeys: Object.keys(body),
      bodyPreview: {
        origin,
        priorAgentKey,
        hasActiveItinerary,
        hasActiveFlights,
        hasActiveStays,
        userInputLen: effectivePrompt.length,
        messagesLen: messages.length,
        sessionId: body.sessionId,
      },
    });
    handleOpenSocketConnection(body);
  }

  const [status, setStatus] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const [transcription, setTranscription] = useState(""); // Store transcribed text
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const pcRef = useRef<any>(null);
  const dcRef = useRef<any>(null);
  const streamRef = useRef<any>(null);

  async function init() {
    setStatus(`${CHAT_BOT_NAME.name} is Ready to Start...`);
    setChatData([]);
    // Route to /chat (TopDestination) — that's the only page that renders
    // chatData, which is where DataChannel transcripts get pushed. The old
    // navigate(ROUTES.Home) / setFirstScreen(NewTrip) sent the user to pages
    // that don't render chatData, so even though transcripts were appended
    // they were invisible.
    if (isMobile) navigate(ROUTES.TopDestination);
    else setFirstScreen(ScreenTypes.topDestination);
    // try {
    setStatus("Fetching ephemeral key...");
    setTranscription("");
    const data = await chatPocVoiceApi(accessJWT);
    const EPHEMERAL_KEY = data?.data?.client_secret.value;
    // data.client_secret.value;
    console.log(EPHEMERAL_KEY);

    const pc = new RTCPeerConnection();
    pcRef.current = pc;

    const audioEl = document.createElement("audio");
    audioEl.autoplay = true;

    pc.ontrack = (e: any) => {
      setAudioStream(e.streams[0]);
      audioEl.srcObject = e.streams[0];
      setStatus("Connected to audio stream.");
      setIsConnected(true);
    };

    setStatus("Requesting microphone access...");
    const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = ms; // Store stream reference
    pc.addTrack(ms.getTracks()[0]);

    let dc = pc.createDataChannel("oai-events");
    dcRef.current = dc; // Store DataChannel reference

    function sendMessage(message: any) {
      if (dc?.readyState === "open") {
        dc.send(JSON.stringify(message));
        console.log("Sent message:", message);
      }
    }
    let searchStatusInterval = null;

    function sendResponseCreate() {
      sendMessage({ type: "response.create" });
    }
    function sendSearchStatus(callId: any) {
      if (dcRef.current?.readyState === "open") {
        const statusMessage = {
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: callId,
            output: JSON.stringify({
              message: "Still searching for flights, please wait...",
            }),
          },
        };
        dcRef.current.send(JSON.stringify(statusMessage));
        sendResponseCreate();

        console.log("Sent search status update...");
      }
    }

    function setupDataChannel() {
      dc.onopen = () => {
        console.log("Data channel is open.");
        // Enable input audio transcription so we get
        // `conversation.item.input_audio_transcription.completed` events for
        // each user turn (drives the user-side chat bubble). Default Realtime
        // sessions don't transcribe input audio.
        dc.send(
          JSON.stringify({
            type: "session.update",
            session: {
              input_audio_transcription: { model: "whisper-1" },
            },
          })
        );
        const conversationMessage = {
          type: "conversation.item.create",
          previous_item_id: null,
          item: {
            id: "msg_" + Date.now(),
            role: "user",
            type: "message",
            content: [
              {
                type: "input_text",
                text: "Announce yourself as Zoya and tell that I'm here to help you with your flights and hotels search? Talk naturally in very friendly tone.",
              },
            ],
          },
        };
        dc.send(JSON.stringify(conversationMessage));
        sendResponseCreate();
      };

      function sendFunctionOutput(callId: any, data: any) {
        const responseMessage = {
          type: "conversation.item.create",
          item: {
            type: "function_call_output",
            call_id: callId,
            output: JSON.stringify(data),
          },
        };
        console.log("responseMessage: ", responseMessage);
        sendMessage(responseMessage);
        sendResponseCreate();
      }

      dc.onerror = (error) => {
        console.error("Data channel error:", error);
        setStatus("Data channel error. Please try again.");
      };

      dc.onmessage = async (e) => {
        try {
          const msg = JSON.parse(e.data);
          // eslint-disable-next-line no-console
          console.log("[Realtime DC]", msg.type, msg);

          // User's spoken prompt — OpenAI Realtime emits this once per turn
          // when input audio transcription completes. Push as a user chat
          // bubble so the mic flow looks like a real conversation in chat.
          // Requires input_audio_transcription enabled via session.update
          // (we enable it in dc.onopen below).
          if (msg.type === "conversation.item.input_audio_transcription.completed") {
            const userText = (msg.transcript || "").trim();
            if (userText) {
              setChatData((prev: iChatData[]) => [
                ...prev,
                {
                  id: v4(),
                  message: userText,
                  isUserMessage: true,
                  isLoading: false,
                  prompt: [],
                  destinationList: [],
                  singleSelectedDestination: false,
                },
              ]);
            }
          }

          // Assistant audio transcript — single source of truth for the
          // assistant chat bubble. Fires once per assistant turn with a
          // flat `transcript` field. Confirmed via the [Realtime DC] log.
          // Do NOT also handle response.output_item.done — that fires for
          // the same turn and would duplicate every assistant message.
          if (msg.type === "response.audio_transcript.done") {
            const trimmed = (msg.transcript || "").trim();
            if (trimmed) {
              setTranscription((prev: any) => prev + " " + trimmed);
              setChatData((prev: iChatData[]) => [
                ...prev,
                {
                  id: v4(),
                  message: trimmed,
                  isUserMessage: false,
                  isLoading: false,
                  prompt: [],
                  destinationList: [],
                  singleSelectedDestination: false,
                },
              ]);
            }
          }

          // Check if the message contains a function call
          if (msg.type === "response.done" && msg.response.output) {
            for (const item of msg.response.output) {
              if (
                item.type === "function_call" &&
                item.name === "search_flights"
              ) {
                const args = JSON.parse(item.arguments);
                args.userId = "4934da0c-5b17-4618-a41a-3d165b055df3";
                console.log("args: ", args);
                searchStatusInterval = setInterval(
                  () => sendSearchStatus(item.call_id),
                  4000,
                );
                const response = await axios.post(
                  `${ENDPOINT}/app/v1/flight/search`,
                  encryptData(args),
                  {
                    headers: {
                      "Content-Type": "text/plain",
                      "x-api-key": API_KEY,
                      "x-access-token": accessJWT,
                    },
                    maxBodyLength: Infinity,
                  },
                );

                if (response.status === 200) {
                  const decryptedData = decryptData(response.data);
                  sendFunctionOutput(item.call_id, decryptedData);
                  clearInterval(searchStatusInterval);
                  sendResponseCreate();
                }
              } else if (
                item.type === "function_call" &&
                item.name === "search_hotels"
              ) {
                const args = JSON.parse(item.arguments);
                console.log("args: ", args);
                searchStatusInterval = setInterval(
                  () => sendSearchStatus(item.call_id),
                  4000,
                );

                const response = await axios.post(
                  `${ENDPOINT}/app/v1/hotel/search`,
                  encryptData(args),
                  {
                    headers: {
                      "Content-Type": "text/plain",
                      "x-api-key": API_KEY,
                      "x-access-token": accessJWT,
                    },
                    maxBodyLength: Infinity,
                  },
                );

                if (response.status === 200) {
                  const decryptedData = decryptData(response.data);
                  sendFunctionOutput(item.call_id, decryptedData);
                  clearInterval(searchStatusInterval);
                  sendResponseCreate();
                }
              }
            }
          }
        } catch (error) {
          console.error("Error processing message:", error);
        }
      };
    }

    setupDataChannel();

    setStatus("Creating offer...");
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const baseUrl = "https://api.openai.com/v1/realtime";
    const model = "gpt-4o-realtime-preview-2024-12-17";

    setStatus("Sending offer to server...");

    const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
      method: "POST",
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${EPHEMERAL_KEY}`,
        "Content-Type": "application/sdp",
      },
    });

    const answer: any = { type: "answer", sdp: await sdpResponse.text() };
    await pc.setRemoteDescription(answer);
    setStatus(`${CHAT_BOT_NAME.name} is Active.`);
    setIsVoiceActive(true);
    // } catch (error: any) {
    //   setStatus(`Error: ${error.message}`);
    //   console.error(error);
    // }
  }

  function stopVoice() {
    setStatus("");
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track: any) => track.stop()); // Stop microphone access
    }
    if (pcRef.current) {
      pcRef.current.close(); // Close WebRTC connection
      pcRef.current = null;
    }
    // setStatus("Zoya is Inactive.");
    setIsConnected(false);
    setIsVoiceActive(false);
  }

  function formatText(input: string) {
    return input
      .replace(/(?<!\d)\.\s*/g, ".\n\n")
      .replace(/([!?])\s*/g, "$1\n")
      .replace(/([^0-9]):([^0-9])/g, "$1:\n$2")
      .replace(/\n{3,}/g, "\n\n")
      .split("\n")
      .map((line, index) => (
        <h1
          // className={styles.text}
          key={index}
        >
          {line
            .split(/(\*\*.*?\*\*)/)
            .map((part, i) =>
              part.startsWith("**") && part.endsWith("**") ? (
                <strong key={i}>{part.slice(2, -2)}</strong>
              ) : (
                part
              ),
            )}
          <br />
        </h1>
      ));
  }

  // VOICE_INPUT_MODE config gate (src/constant/environment.ts, default "transcript"):
  // "realtime" exposes init/stopVoice/isVoiceActive so mic input uses the OpenAI
  // Realtime WebRTC session; any other value (default) leaves them undefined/false
  // so every consumer (InputWithMic/InputWithMicNew via useRecordingHooks'
  // `if (initVoiceTranscript)` branch) falls back to browser Web Speech transcription.
  const isRealtimeVoiceEnabled = VOICE_INPUT_MODE === "realtime";
  const value = {
    handleNewTripSendMessage,
    handleTopDestinationSendMessage,
    formatText,
    transcription,
    init: isRealtimeVoiceEnabled ? init : undefined,
    stopVoice: isRealtimeVoiceEnabled ? stopVoice : undefined,
    isVoiceActive: isRealtimeVoiceEnabled ? isVoiceActive : false,
    status,
  };
  return <PocContext.Provider value={value}>{children}</PocContext.Provider>;
};
export const usePocContext = () => useContext(PocContext);
